// Caminho: vita-frontend/src/pages/Client/ClientHomePage.js

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { getAllProjects } from "../../services/projectService";
import { useAuth } from "../../contexts/AuthContext";
import { getDisplayName } from "../../utils/usersUtils";
import StatCard from "../../components/cards/StatCard";
import { FaBell } from "react-icons/fa";
import ProjectNotificationsPanel from "../../components/layout/ProjectNotificationsPanel";

// --- Styled Components (mantidos) ---
const HomePageWrapper = styled.div`
    padding: 24px;
`;

const StatsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 24px;
    margin-bottom: 32px;
`;

const Title = styled.h1`
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: ${({ theme }) => theme.spacing.large};
`;
// ... (O restante dos seus styled-components permanece igual)
const CardsContainer = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing.large};
    margin-bottom: ${({ theme }) => theme.spacing.large};
    flex-wrap: wrap;
`;

const TableWrapper = styled.div`
    background-color: ${({ theme }) => theme.colors.white};
    border-radius: ${({ theme }) => theme.borderRadius};
    padding: ${({ theme }) => theme.spacing.large};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    overflow-x: auto;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th,
    td {
        padding: 12px 15px;
        text-align: left;
        border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
    }

    th {
        background-color: #f8f9fa;
        font-weight: bold;
        color: ${({ theme }) => theme.colors.primary};
    }

    tbody tr {
        cursor: pointer;
        &:hover {
            background-color: #f1f1f1;
        }
    }
`;

const RecentProjects = styled.div`
    background-color: white;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
    margin-top: 0;
    margin-bottom: 20px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    th,
    td {
        padding: 12px 15px;
        text-align: left;
        border-bottom: 1px solid #f1f1f1;
    }
    th {
        font-weight: bold;
        color: #800020;
    }
    tbody tr {
        cursor: pointer;
        &:hover {
            background-color: #f9f9f9;
        }
    }
`;

const StatusBadge = styled.span`
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
    color: white;
    background-color: ${({ status }) => {
        switch (status) {
            case "Em andamento":
                return "#6c757d";
            case "Em atraso":
                return "#ffc107";
            case "Finalizado":
                return "#28a745";
            default:
                return "#6c757d";
        }
    }};
`;

const ButtonsContainer = styled.div`
    margin-top: ${({ theme }) => theme.spacing.large};
    display: flex;
    justify-content: center;
`;

const ActionButton = styled.button`
    padding: 12px 24px;
    font-size: 16px;
    font-weight: bold;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
`;

// --- Helpers ---
const stripRef = (ref) => {
    if (!ref || typeof ref !== "string") return null;
    return ref.includes("/") ? ref.split("/").pop() : ref;
};

// --- Componente da Página ---
const ClientHomePage = () => {
    const { user } = useAuth(); // Pega o cliente logado
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const projectsData = await getAllProjects();
                setProjects(projectsData.projects || []);
            } catch (err) {
                console.error(err);
                setError("Não foi possível carregar os dados.");
                setProjects([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);


    const projectStats = useMemo(() => {
        if (!Array.isArray(projects) || projects.length === 0) {
            return { inProgress: 0, delayed: 0, completed: 0 };
        }

        return projects.reduce(
            (acc, item) => {
                // LÓGICA ATUALIZADA: Acessa o status dentro de item.project
                const status = item?.status || "";
                const s = status.toLowerCase();
                if (s.includes("final") || s.includes("conclu"))
                    acc.completed++;
                else if (s.includes("atras")) acc.delayed++;
                else if (s.includes("andam") || s.includes("em andamento"))
                    acc.inProgress++;
                return acc;
            },
            { inProgress: 0, delayed: 0, completed: 0 }
        );
    }, [projects]);

    const recentProjects = useMemo(() => {
        if (!Array.isArray(projects) || projects.length === 0) {
            return [];
        }

        return projects
            .slice()
            .sort(
                (a, b) =>
                    new Date(b.startDate) -
                    new Date(a.startDate)
            )
            .slice(0, 5);
    }, [projects]);

    const handleNotificationClick = (project, e) => {
        e.stopPropagation();
        setSelectedProject(project); // Passa o objeto do projeto para o painel
        setIsPanelOpen(true);
    };

    if (loading) return <div>Carregando...</div>;
    if (error) return <div style={{ color: "red" }}>{error}</div>;

    return (
        <div>
            <Title>Início</Title>
            <CardsContainer>
                <StatCard
                    title="Projetos em Andamento"
                    count={projectStats.inProgress}
                    color="#E0E0E0"
                />
                <StatCard
                    title="Projetos em Atraso"
                    count={projectStats.delayed}
                    color="#FFD700"
                />
                <StatCard
                    title="Projetos Concluídos"
                    count={projectStats.completed}
                    color="#05c85aff"
                />
            </CardsContainer>
            <Title as="h2" style={{ fontSize: "24px" }}>
                Seus Projetos
            </Title>
            <TableWrapper>
                <StyledTable>
                    <thead>
                        <tr>
                            <th>Nome do Projeto</th>
                            <th>Responsável</th>
                            <th>Data de Início</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {projects.length > 0 ? (
                            recentProjects.map((item) => (
                                <tr
                                    key={item.id}
                                    onClick={() =>
                                        navigate(
                                            `/client/projeto/${item.id}`
                                        )
                                    }
                                >
                                    <td>{item.name}</td>
                                    <td>
                                        {item.employee
                                            ? getDisplayName(
                                                  item.employee
                                              )
                                            : "N/A"}
                                    </td>
                                    <td>
                                        {item.startDate
                                            ? new Date(
                                                item.startDate
                                            ).toLocaleDateString()
                                            : "N/A"}
                                    </td>
                                    <td>
                                        <StatusBadge
                                            status={item.status}
                                        >
                                            {item.status}
                                        </StatusBadge>
                                    </td>
                                    <td
                                        onClick={(e) =>
                                            handleNotificationClick(
                                                item.project,
                                                e
                                            )
                                        }
                                    >
                                        <FaBell
                                            style={{
                                                cursor: "pointer",
                                                fontSize: "18px",
                                            }}
                                        />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={{ textAlign: "center" }}>
                                    Você ainda não possui projetos.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </StyledTable>
                <ButtonsContainer>
                    <ActionButton onClick={() => navigate("/client/pesquisa")}>
                        Ver Todos os Projetos
                    </ActionButton>
                </ButtonsContainer>
            </TableWrapper>
            {selectedProject && (
                <ProjectNotificationsPanel
                    isOpen={isPanelOpen}
                    onClose={() => setIsPanelOpen(false)}
                    projectId={selectedProject.id}
                    projectName={selectedProject.name}
                />
            )}
        </div>
    );
};

export default ClientHomePage;
